import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { formatDate } from "../api";
import { useAuth } from "../auth";
import { useApi } from "../hooks";

type Feed = { label: string; status: "ok" | "stale" | "failing" | "needs_key" | "not_connected" | "unknown"; reason?: string | null; last_good_at?: string | null };
export type FeedKey = "google_ads" | "ctm" | "ghl";
type Health = Partial<Record<FeedKey, Feed>>;

// Where the fix lives, in the words a person clicks through.
const FIX: Record<FeedKey, { text: string; to: string }> = {
  google_ads: { text: "Reconnect Google", to: "/settings#integrations" },
  ctm: { text: "Update the CTM key", to: "/settings#integrations" },
  ghl: { text: "Open GHL settings", to: "/client-settings" },
};

// What a page loses when a feed stops.
const CONSEQUENCE: Record<FeedKey, string[]> = {
  google_ads: ["ad spend"],
  ctm: ["calls"],
  ghl: ["bookings"],
};
const joinWords = (words: string[]) =>
  words.length <= 1 ? words.join("") : `${words.slice(0, -1).join(", ")} and ${words.at(-1)}`;

// What each feed fills in, named so a notice says which numbers stopped, not "numbers here".
const STOPPED: Record<FeedKey, string> = {
  google_ads: "Google Ads spend and clicks",
  ctm: "CTM calls",
  ghl: "GoHighLevel bookings",
};
const CURRENT: Record<FeedKey, string> = {
  google_ads: "Spend and clicks",
  ctm: "Calls",
  ghl: "Bookings",
};
/** "Aug 26": this year's days need no year. */
const day = (iso: string) => {
  const text = formatDate(iso);
  return new Date(iso).getFullYear() === new Date().getFullYear() ? text.replace(/, \d{4}$/, "") : text;
};

function sentenceFor(key: FeedKey, f: Feed) {
  const since = f.last_good_at ? ` ${STOPPED[key]} stop at ${day(f.last_good_at)}.` : "";
  switch (f.status) {
    case "needs_key": return `${f.label} isn't updating: the saved key or token was refused.${since}`;
    case "failing": return `${f.label}'s last sync failed.${since}`;
    case "stale": return `${f.label} hasn't synced since ${f.last_good_at ? day(f.last_good_at) : "it was set up"}.`;
    case "not_connected": return `${f.label} isn't connected yet, so this stays empty until it is.`;
    default: return null;
  }
}

/**
 * A placeholder where a page's numbers depend on a feed that is not updating
 * (GET /api/connections/health), so a 0 reads as "waiting on CTM", not "no calls".
 * Renders nothing while every listed feed is fine. Several stopped feeds (and
 * `also`, another stopped source the page knows about) share one box, one line
 * each, instead of stacking a yellow box per feed.
 */
export default function ConnectionNotice({ feeds, compact, clientId, also }: { feeds: FeedKey[]; compact?: boolean; clientId?: number | null; also?: ReactNode }) {
  const { user } = useAuth();
  // On a client's page, that client's own feeds; elsewhere the agency's.
  const health = useApi<Health>(clientId ? `/api/connections/health?client_id=${clientId}` : "/api/connections/health");
  // A feed this client never had (not connected) is said by the page's own empty state.
  const broken = feeds.map((k) => [k, health.data?.[k]] as const).filter((x): x is readonly [FeedKey, Feed] => !!x[1] && !!sentenceFor(x[0], x[1]) && !(clientId && x[1].status === "not_connected"));
  if (!broken.length && !also) return null;
  // The page's other feeds that are fine, said once, so one stopped feed does not read as the whole page.
  const current = feeds.filter((k) => health.data?.[k]?.status === "ok").map((k) => CURRENT[k]);
  const stillCurrent = current.length ? ` ${joinWords(current.map((w, i) => (i ? w.toLowerCase() : w)))} ${current.length === 1 && !/s$/.test(current[0]) ? "is" : "are"} current.` : "";
  const admin = user?.role === "superadmin";
  if (compact && broken.length) {
    // Name what is actually missing, from the feeds that are down, and link to
    // where that feed is fixed (GHL tokens live in each client's settings).
    const missing = [...new Set(broken.flatMap(([key]) => CONSEQUENCE[key]))];
    const labels = broken.map(([, f]) => f.label);
    const fix = broken.length === 1 ? FIX[broken[0][0]] : { text: "Settings → Integrations", to: "/settings#integrations" };
    return (
      <p className="inline-notice warning connection-notice" role="status">
        <span>
          {labels.length === 1 ? `${labels[0]} isn't updating` : `${labels.slice(0, -1).join(", ")} and ${labels.at(-1)} aren't updating`}, so {joinWords(missing)} {missing.length === 1 && !missing[0].endsWith("s") ? "is" : "are"} incomplete.{" "}
          {admin && <Link to={fix.to}>{broken.length === 1 ? fix.text : "Open Settings"}</Link>}
          {admin && broken.some(([key]) => key === "ghl") && broken.length > 1 && <>. GHL tokens are in each client's Setup → Connections.</>}
        </span>
      </p>
    );
  }
  const lines = broken.map(([key, f], i) => (
    <span key={key} title={f.reason || undefined}>
      {sentenceFor(key, f)}{stillCurrent}{" "}
      {admin
        ? <Link to={key === "ghl" && clientId ? `${FIX.ghl.to}?client_id=${clientId}` : FIX[key].to}>{FIX[key].text}</Link>
        // Said once for the whole box, after its last stopped feed.
        : i === broken.length - 1 && "An admin can fix this in Settings."}
    </span>
  ));
  if (also) lines.push(<span key="also">{also}</span>);
  return lines.length === 1
    ? <p className="inline-notice warning connection-notice" role="status">{lines[0]}</p>
    : <div className="inline-notice warning connection-notice notice-lines" role="status">{lines}</div>;
}
